import React, {startTransition, useActionState, useCallback, useMemo, useOptimistic} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';
import {useExpressPaymentMethods} from 'commerceAPI/hooks';

import {EEventSource} from 'coreClasses/events/IEvents';
import {useApplePay, useBannedProduct, useCustomerMgr, useMedia, useNotify, useProductSubscription, useSetUI} from 'coreHooks';
import {ExpressPaymentButtonSection} from 'coreStore/data/basket/types';
import {EAddToCartType} from 'coreStore/data/ui/types';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';
import ExpressPaymentButtons from 'brandComponents/ExpressPaymentButtons';
import AddToCartButton, {AddToCartButtonMode} from 'brandComponents/product/AddToCartButton';
import {NEWSLETTER_SOURCE} from 'brandConstants';
import {useAddToCart, useBasket} from 'brandHooks';
import type {TAddToCartItem} from 'brandHooks/useAddToCart';

import {getPayPalButtonStylesBySection} from './helpers';
import {IAddToCartWrapperProps} from './IAddToCartWrapper';
import messages from './messages';

import styles from './AddToCartWrapper.module.scss';

/** Added from the button (variant known): the others go through `UiMgr.setProductToAddToCart` and `useShopper`. */
const DIRECT_ADD_TYPES = [EAddToCartType.ADD_TO_CART, EAddToCartType.BUY_NOW, EAddToCartType.MOVE_FROM_WISHLIST];

const AddToCartWrapper = ({
	classNames,
	product,
	variationGroupId,
	overrideButtonsConfigurations,
	extraPaymentSection = ExpressPaymentButtonSection.PDP,
	variant = 'secondary',
	labels,
	source,
	parent = null,
}: IAddToCartWrapperProps) => {
	const setUI = useSetUI();
	const {managerRepository} = useGeneralProvider();
	const defaultConfigurations = {
		buyNow: false,
		moveFromWishlist: false,
		expressPayment: false,
	};
	const buttonsConfigurations = useMemo(() => {
		return {
			...defaultConfigurations,
			...overrideButtonsConfigurations,
		};
	}, [overrideButtonsConfigurations]);
	variationGroupId = variationGroupId || product?.getVariationGroupId();

	const isPreorder = product.isPreOrderShown();
	const isOrderable = product?.isOrderable();
	const CustomerMgr = useCustomerMgr();
	const customer = CustomerMgr.getCurrentCustomer();
	const productSubscription = useProductSubscription();
	const nlSubscriptionSrc: string = EEventSource[EEventSource.NOTIFY_ME_POPUP];
	const intl = useIntl();
	const notify = useNotify();
	const basket = useBasket();
	const media = useMedia();
	const applePayProcessor = useApplePay();
	const isMobile = media('<=small');
	const expressPaymentMethods = useExpressPaymentMethods(extraPaymentSection, product.id);
	const {isBannedB} = useBannedProduct(product);
	const addToCart = useAddToCart();
	// Button of the add running (spinner, not on the wishlist button), back to `null` when the action ends.
	const [pendingType, setPendingType] = useOptimistic<EAddToCartType | null>(null);
	const [, dispatchAddToCart, isPending] = useActionState(async (_: null, item: TAddToCartItem) => {
		setPendingType(item.type);
		await addToCart(item);

		return null;
	}, null);

	const storeProductToAddToCart = (
		productId: string,
		variationGroupId: string,
		size: string,
		buttonType: EAddToCartType,
		source?: EEventSource,
	) => {
		const productItem = basket.productItems.find((item) => item.getProductId() === productId);
		const stockLevel = product?.getAtsStockLevel?.() || 0;

		if (productItem && productItem.getQuantity() === stockLevel) {
			notify.error(intl.formatMessage(messages.quantityError));

			return;
		}

		if (productId && DIRECT_ADD_TYPES.includes(buttonType)) {
			const type = buttonType as TAddToCartItem['type'];

			startTransition(() => dispatchAddToCart({productId, variationGroupId, size, quantity: 1, type, source}));

			return;
		}

		managerRepository.getUiMgr().setProductToAddToCart(
			{
				productId,
				variationGroupId: variationGroupId,
				size,
				quantity: 1,
				type: buttonType,
				source,
			},
			parent,
		);
	};

	// This function is responsible for determining specific variationID
	const handleClick = (buttonType: EAddToCartType = EAddToCartType.ADD_TO_CART) => {
		if (!isOrderable && (product.isVariant() || product.isSingleVariant())) {
			const productId = product.isVariant() ? product.getProductId() : product.variants[0].productId;

			if (customer?.isRegistered) {
				const newsletterSource = {
					newsletterSource: NEWSLETTER_SOURCE[nlSubscriptionSrc],
					newsletterSourceDetails: {
						pageType: NEWSLETTER_SOURCE.CUSTOM_NOTIFY_ME,
					},
				};
				productSubscription
					.subscribe(customer.getEmail(), productId, true, newsletterSource)
					.then(() => {
						notify.success(intl.formatMessage(messages.notifyMeSuccess));
					})
					.catch(() => {
						notify.error(intl.formatMessage(messages.notifyMeError));
					});
			} else {
				setUI.showNotifyMePopup(variationGroupId, productId);
			}
		} else {
			let productId = null;

			if (product.isVariant()) {
				productId = product.getProductId();

				const size = product.getSize();

				storeProductToAddToCart(productId, variationGroupId, size, buttonType, source);
			} else if (product.isSingleVariant()) {
				productId = product.product.variants[0].productId;

				const size = product.product.variants[0].variationValues.size;

				if (buttonType === EAddToCartType.APPLEPAY && applePayProcessor) {
					/**
					 * Fix for Apple Pay, blocked for security reason if no sizeSelector is shown.
					 * Error: "InvalidAccessError: Must create a new ApplePaySession from a user gesture handler."
					 */
					applePayProcessor.applePayHandler(productId);
				} else {
					storeProductToAddToCart(productId, variationGroupId, size, buttonType, source);
				}
			} else {
				storeProductToAddToCart(productId, variationGroupId, null, buttonType, source);
			}
		}
	};

	// The wishlist button keeps its look while the item moves (no spinner, no grey of `disabled`): a click while pending adds nothing.
	const handleClickMoveFromWishlist = useCallback(
		() => !isPending && handleClick(EAddToCartType.MOVE_FROM_WISHLIST),
		[handleClick, isPending, EAddToCartType.MOVE_FROM_WISHLIST],
	);

	if (isBannedB) {
		return null;
	}

	return (
		<div
			className={cn(
				styles['add-to-cart__button-wrap'],
				'add-to-cart__button-wrap',
				classNames?.addToCartWrpp,
			)}
		>
			{buttonsConfigurations.moveFromWishlist && (
				<AddToCartButton
					classNames={cn(
						'move-wishlist-item-to-basket',
						styles['add-to-cart__button__wishlist-to-basket'],
						classNames?.generalButton
					)}
					onClick={handleClickMoveFromWishlist}
					label={labels?.addToCart}
					mode={isPreorder ? AddToCartButtonMode.PREORDER_NOW : AddToCartButtonMode.ADD_TO_CART}
					variant={variant}
					product={product}
				/>
			)}
			{!buttonsConfigurations.moveFromWishlist && (
				<AddToCartButton
					classNames={cn(styles['add-to-cart__button'], classNames?.generalButton, {
						'add-to-cart__notify-me': !isOrderable,
					})}
					onClick={handleClick}
					disabled={isPending}
					loading={pendingType === EAddToCartType.ADD_TO_CART}
					label={isPreorder ? labels?.preOrderNow : labels?.addToCart}
					mode={isPreorder ? AddToCartButtonMode.PREORDER_NOW : AddToCartButtonMode.ADD_TO_CART}
					variant={variant}
					product={product}
				/>
			)}
			{!expressPaymentMethods.length && buttonsConfigurations.buyNow && isOrderable && !isPreorder && (
				<AddToCartButton
					classNames={cn(styles['add-to-cart__button__buy-now'], classNames?.generalButton)}
					onClick={handleClick}
					disabled={isPending}
					loading={pendingType === EAddToCartType.BUY_NOW}
					label={labels?.buyNow}
					mode={AddToCartButtonMode.BUY_NOW}
					variant={variant}
					product={product}
				/>
			)}
			{buttonsConfigurations.expressPayment &&
				!!expressPaymentMethods.length
				&& isOrderable
				&& !isPreorder
				&& (
					<ExpressPaymentButtons
						product={product}
						expressPaymentMethods={expressPaymentMethods}
						section={extraPaymentSection}
						onClick={handleClick}
						overridePayPalButtonStyles={getPayPalButtonStylesBySection(
							extraPaymentSection,
							isMobile
						)}
						isProduct={true}
					/>
				)
			}
		</div>
	);
};

export default AddToCartWrapper;
